/**
 * Friendly input fields for a strategy's `params` object.
 *
 * Every field reads and writes the SAME params object the JSON textarea beside
 * it shows, so the two stay in sync in both directions — type in the box and the
 * fields follow; tick a checkbox and the JSON updates. The textarea stays the
 * escape hatch for anything that has no field (and for a param added after this
 * file was last touched).
 *
 * Nothing here is keyed on the strategy KEY. Every field and every section is
 * duck-typed on the presence of the param it edits, which has two consequences
 * worth keeping:
 *   - a strategy that does not declare a param never shows its control, so the
 *     form can never post a key the schema would reject (the bug that had the
 *     `loss_recovery` checkbox rendering for `rupee_five_strangle`);
 *   - a param shared by several strategies gets its field for free everywhere.
 *     Golden Number and Daily HTP Ladders share ~32 params, so one definition
 *     covers both, and Daily's own extras (the MTF timeframes, `max_flips`)
 *     appear only where they exist.
 *
 * `context` only decides whether ORDER ROUTING is shown: a deployment routes
 * orders through these params, while the backtest form carries its own "Trade
 * on" control that writes a nested `__routing__` block instead, so showing both
 * there would be two controls for one thing.
 */
import { useMemo, useRef } from "react";
import Card from "./Card";
import { NumberInput, TimeInput, noAutocorrect } from "./NumberInput";

export type ParamFieldsContext = "deployment" | "backtest";

interface Props {
  params: Record<string, unknown>;
  patch: (patch: Record<string, unknown>) => void;
  context: ParamFieldsContext;
  /** "muted" when nesting inside another Card (the backtest form), so it reads
   *  as a sub-panel rather than a second floating card. */
  tone?: "default" | "muted";
  /** The params JSON box does not parse right now. The page passes the last
   *  values that did, and the fields are locked (a write would rebuild the JSON
   *  from those stale values over the user's half-finished edit) — the card
   *  stays put instead of vanishing and jumping the layout mid-typing. */
  disabled?: boolean;
}

const selectClass = "w-full border border-border rounded px-3 py-2";
const inputClass = "w-full border border-border rounded px-3 py-2 font-mono";
// text-base below `sm`: iOS Safari zooms the page on focus for any input under
// 16px and does not zoom back. min-w keeps the ladder cells usable on a phone
// (the table scrolls sideways instead of crushing them).
const smallInputClass =
  "w-full min-w-[5rem] border border-border rounded px-2 py-2 sm:py-1 font-mono text-base sm:text-xs";

/** Timeframe strings the MTF knobs accept; "" means "same as the run's TF".
 *  Must stay in step with `TF_MINUTES` in the strategy — notably there is NO
 *  "1d": these knobs resample an intraday session, and the validator rejects it. */
const TF_CHOICES = ["", "1m", "3m", "5m", "10m", "15m", "30m", "1h"];

/**
 * What the fields should show for a params JSON box: the parsed object while it
 * parses, else the last object that did (with `disabled` set). Spread straight
 * onto <StrategyParamFields>.
 */
export function useParamsFieldsSource(json: string): {
  params: Record<string, unknown>;
  disabled: boolean;
} {
  const parsed = useMemo<Record<string, unknown> | null>(() => {
    try {
      const v = JSON.parse(json || "{}");
      return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }, [json]);
  const lastGood = useRef<Record<string, unknown>>({});
  if (parsed) lastGood.current = parsed;
  return { params: parsed ?? lastGood.current, disabled: parsed === null };
}

export default function StrategyParamFields({
  params,
  patch,
  context,
  tone = "default",
  disabled = false,
}: Props) {
  const has = (k: string) => k in params;
  const s = (k: string, dflt = "") => String(params[k] ?? dflt);
  const n = (k: string, dflt = 0) => {
    const v = params[k];
    return typeof v === "number" ? v : Number(v ?? dflt);
  };
  const b = (k: string, dflt = false) => {
    const v = params[k];
    return typeof v === "boolean" ? v : v === undefined || v === null ? dflt : v === "true";
  };
  const arr = (k: string): number[] => {
    const v = params[k];
    return Array.isArray(v) ? v.map((x) => Number(x) || 0) : [];
  };

  // Nothing to edit — a strategy with none of these params (sma_cross, say)
  // keeps the plain JSON box and no empty card.
  const anySection =
    has("golden_source") ||
    has("trigger_points") ||
    has("target_points") ||
    has("max_loss_mode") ||
    has("session_start_ist") ||
    has("arming_close_tf") ||
    has("resolve_within_points") ||
    (context === "deployment" && has("order_instrument"));
  if (!anySection) return null;

  return (
    <Card
      tone={tone}
      title="Strategy settings"
      subtitle="The same values as the params JSON above — edit either one. Only the settings this strategy actually has are shown."
    >
      {disabled && (
        <p className="text-xs text-down-600 dark:text-down-400 mb-3">
          The params JSON above has a syntax error, so these fields are locked
          until it is fixed. They show the last values that parsed.
        </p>
      )}
      <fieldset disabled={disabled} className="space-y-6 min-w-0 disabled:opacity-60">
        <GoldenSection {...{ patch, has, s, n }} />
        <LadderSection {...{ patch, has, n, arr }} />
        <ValueAreaSection {...{ patch, has, b }} />
        <TargetSection {...{ patch, has, n }} />
        <DaySection {...{ patch, has, s, n, b }} />
        <SessionSection {...{ patch, has, s }} />
        <TimeframeSection {...{ patch, has, s }} />
        <ResolveSection {...{ patch, has, n }} />
        {context === "deployment" && <RoutingSection {...{ patch, has, s, n }} />}
        {context === "backtest" && has("order_instrument") && (
          <Section title="Order routing">
            <p className="col-span-1 sm:col-span-2 text-xs text-muted">
              A backtest&rsquo;s routing is set by the <b>Trade on</b> control in
              this form, not by these params — pick “futures” or “option” there
              to trade like the deployment. The strategy&rsquo;s own{" "}
              <code>order_instrument</code> and strike settings are read only in
              paper/live.
            </p>
          </Section>
        )}
      </fieldset>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* sections                                                            */
/* ------------------------------------------------------------------ */

type Has = (k: string) => boolean;
type Str = (k: string, dflt?: string) => string;
type Num = (k: string, dflt?: number) => number;
type Bool = (k: string, dflt?: boolean) => boolean;
type Patch = (patch: Record<string, unknown>) => void;

function GoldenSection({
  patch,
  has,
  s,
  n,
}: {
  patch: Patch;
  has: Has;
  s: Str;
  n: Num;
}) {
  if (!has("golden_source")) return null;
  const source = s("golden_source", "manual");
  // Both of these are duck-typed rather than keyed on the strategy name.
  // `htp_window_minutes` exists only on Daily HTP Ladders, so it is what tells
  // the two golden-family strategies apart.
  const hasWindowSource = has("htp_window_minutes");
  // `first_candle_htp` is no longer OFFERED on Golden Number (2026-10-08) —
  // manual is the default and the only choice there. It is still fully
  // supported and settable in the params JSON, so it must still RENDER when a
  // config already carries it: a controlled <select> whose value matches no
  // <option> shows as blank and the next change would silently rewrite the
  // saved source. Daily HTP Ladders keeps it as a normal choice.
  const showFirstCandle = hasWindowSource || source === "first_candle_htp";
  const firstCandleIsLegacy = !hasWindowSource;
  // `window_hlc3` follows exactly the same rule, and for the same reason: both
  // strategies ACCEPT it, but Golden Number's dropdown stays manual-only, so it
  // renders there as a JSON-set value rather than an offerable choice. On Daily
  // HTP Ladders it is a normal choice.
  const showHlc3 = hasWindowSource || source === "window_hlc3";
  const hlc3IsLegacy = !hasWindowSource;
  // `window_htp_5m` is JSON-ONLY on BOTH strategies, by request — so unlike
  // the others there is no `hasWindowSource` branch here. It renders only when
  // a saved config already carries it, for the same reason the others do: a
  // controlled <select> whose value matches no <option> renders blank and the
  // next change would silently rewrite the saved source.
  const showHtp5m = source === "window_htp_5m";
  // `hlc35` is JSON-ONLY too, by request (2026-11-02), on all three golden
  // strategies: shown, as its raw value, only when a saved config carries it.
  const showHlc35 = source === "hlc35";
  const manualUnset = source === "manual" && !(n("golden_number", 0) > 0);
  return (
    <Section
      title="The golden number"
      note="Fixed once a day; every band, ladder and stop is measured from it."
    >
      <Field label="How it is set">
        <select
          value={source}
          onChange={(e) => patch({ golden_source: e.target.value })}
          className={selectClass}
        >
          <option value="manual">A number I set myself</option>
          {hasWindowSource && (
            <option value="window_high_low">
              Opening window&rsquo;s high/low midpoint
            </option>
          )}
          {/* A JSON-set source shows its raw JSON value and nothing else. */}
          {showFirstCandle && (
            <option value="first_candle_htp">
              {firstCandleIsLegacy
                ? "first_candle_htp"
                : "First candle\u2019s volume-profile POC"}
            </option>
          )}
          {showHlc3 && (
            <option value="window_hlc3">
              {hlc3IsLegacy
                ? "window_hlc3"
                : "First 5 minutes\u2019 HLC3 (published 09:20 level)"}
            </option>
          )}
          {showHtp5m && <option value="window_htp_5m">window_htp_5m</option>}
          {showHlc35 && <option value="hlc35">hlc35</option>}
        </select>
        {source === "hlc35" && (
          <p className="text-xs text-fg-subtle mt-1">
            <b>(High + Low + Close) / 3 of the session&rsquo;s first five
            minutes, rounded to the nearest whole point</b> (a half rounds up).
            Always 09:15&ndash;09:19, whatever timeframe the run uses, and the
            same whenever the deployment was started. It exists from 09:20, so
            the first arming waits for a candle that closes at or after 09:20
            &mdash; 09:21 on a 3-minute run.
          </p>
        )}
        {source === "window_hlc3" && !hlc3IsLegacy && (
          <p className="text-xs text-fg-subtle mt-1">
            <b>(High + Low + Close) / 3 of the session&rsquo;s first five
            minutes</b> — the formula that reproduces the level published at
            09:20, matched against 42 days of real numbers to within{" "}
            <b>0.88 points</b> on average (the volume-profile POC is ~10 points
            off). The window is a fixed five minutes whatever timeframe the run
            uses. Because the level does not exist until 09:20, the first arming
            of each day waits for a candle that closes at or after 09:20.
          </p>
        )}
      </Field>
      {source === "manual" && (
        <Field label="Golden number">
          <NumberInput
            step="any"
            // Not 0: the schema's default is a placeholder 0, and since manual
            // became the default source that is what a fresh form seeds. Every
            // band is measured from this, so 0 arms the day at meaningless
            // levels — the browser blocks submit instead.
            min={0.0001}
            required
            value={n("golden_number", 0)}
            onChange={(v) => patch({ golden_number: v })}
            className={inputClass}
          />
          {manualUnset ? (
            <p className="text-xs text-down-600 dark:text-down-400 mt-1">
              <b>Set this before saving.</b> It defaults to 0, which is not a
              usable level — every band and ladder is measured from the golden
              number, so 0 leaves price permanently past the trigger.
            </p>
          ) : (
            <p className="text-xs text-fg-subtle mt-1">
              Used for every day of the run, so a single number suits a
              single-day test; across a longer run it is the same level daily.
            </p>
          )}
        </Field>
      )}
      {has("htp_window_minutes") && (
        <Field label="Opening window (minutes)">
          <NumberInput
            min={0}
            max={375}
            value={n("htp_window_minutes", 0)}
            onChange={(v) => patch({ htp_window_minutes: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            How much of the session the golden number is computed from. 0 uses
            the first candle alone.
          </p>
        </Field>
      )}
      {has("arm_delay_minutes") && (
        <Field label="Wait before arming (minutes)">
          <NumberInput
            min={0}
            max={375}
            value={n("arm_delay_minutes", 0)}
            onChange={(v) => patch({ arm_delay_minutes: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            No direction is taken until this long after the open, however the
            price closes. 0 arms from the second candle.
          </p>
        </Field>
      )}
    </Section>
  );
}

function LadderSection({
  patch,
  has,
  n,
  arr,
}: {
  patch: Patch;
  has: Has;
  n: Num;
  arr: (k: string) => number[];
}) {
  if (!has("trigger_points") && !has("ladder_points")) return null;
  const points = arr("ladder_points");
  const pcts = arr("ladder_percents");
  const aggr = arr("aggressive_ladder_percents");
  const hasAggr = has("aggressive_ladder_percents");

  /** The three arrays must stay the same length — the strategy's own validator
   *  rejects a mismatch — so rows are added and removed together rather than
   *  letting the user edit three independent JSON lists into disagreement. */
  function setRung(i: number, field: "pt" | "pct" | "ag", value: number) {
    const next: Record<string, unknown> = {};
    if (field === "pt") next.ladder_points = points.map((v, j) => (j === i ? value : v));
    if (field === "pct") next.ladder_percents = pcts.map((v, j) => (j === i ? value : v));
    if (field === "ag")
      next.aggressive_ladder_percents = aggr.map((v, j) => (j === i ? value : v));
    patch(next);
  }

  function addRung() {
    const lastPt = points.length ? points[points.length - 1] : 0;
    patch({
      ladder_points: [...points, lastPt + 10],
      ladder_percents: [...pcts, 0],
      ...(hasAggr ? { aggressive_ladder_percents: [...aggr, 0] } : {}),
    });
  }

  function removeRung(i: number) {
    patch({
      ladder_points: points.filter((_, j) => j !== i),
      ladder_percents: pcts.filter((_, j) => j !== i),
      ...(hasAggr
        ? { aggressive_ladder_percents: aggr.filter((_, j) => j !== i) }
        : {}),
    });
  }

  const pctSum = pcts.reduce((a, c) => a + c, 0);
  const aggrSum = aggr.reduce((a, c) => a + c, 0);

  return (
    <Section
      title="Direction and the entry ladder"
      note="What arms a side, and where each tranche waits for price to come back."
    >
      {has("trigger_points") && (
        <Field label="Trigger (points beyond the golden number)">
          <NumberInput
            step="any"
            min={0.1}
            max={500}
            value={n("trigger_points", 6)}
            onChange={(v) => patch({ trigger_points: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            A candle closing at-or-beyond <b>golden + this</b> goes bullish, and
            at-or-beyond <b>golden − this</b> bearish. It is also the
            stop/reversal threshold on the other side, so widening it both
            delays entries and gives an open position more room.
          </p>
        </Field>
      )}
      {has("aggressive_candle_points") && (
        <Field label="Aggressive-reversal body (points)">
          <NumberInput
            step="any"
            min={0.1}
            max={5000}
            value={n("aggressive_candle_points", 25)}
            onChange={(v) => patch({ aggressive_candle_points: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            When the candle that flips direction has a body (|open − close|)
            bigger than this, the new ladder uses the aggressive percentages
            column instead of the normal one.
          </p>
        </Field>
      )}
      {has("ladder_points") && (
        <div className="col-span-1 sm:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-fg-subtle">
              Ladder rungs
            </label>
            <button
              type="button"
              onClick={addRung}
              className="text-xs text-fg-subtle hover:underline px-2 py-2 -mr-2 sm:py-1"
            >
              + Add rung
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted border-b border-border/60">
                <tr>
                  <th className="py-1 pr-2 font-normal">Distance from golden</th>
                  <th className="py-1 pr-2 font-normal">% of position</th>
                  {hasAggr && (
                    <th className="py-1 pr-2 font-normal">% when aggressive</th>
                  )}
                  <th className="py-1"></th>
                </tr>
              </thead>
              <tbody>
                {points.map((pt, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0">
                    <td className="py-1 pr-2">
                      <NumberInput
                        step="any"
                        value={pt}
                        onChange={(v) => setRung(i, "pt", v)}
                        className={smallInputClass}
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <NumberInput
                        step="any"
                        min={0}
                        value={pcts[i] ?? 0}
                        onChange={(v) => setRung(i, "pct", v)}
                        className={smallInputClass}
                      />
                    </td>
                    {hasAggr && (
                      <td className="py-1 pr-2">
                        <NumberInput
                          step="any"
                          min={0}
                          value={aggr[i] ?? 0}
                          onChange={(v) => setRung(i, "ag", v)}
                          className={smallInputClass}
                        />
                      </td>
                    )}
                    <td className="py-1 text-right">
                      {points.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRung(i)}
                          aria-label={`Remove rung ${i + 1}`}
                          className="text-down-600 dark:text-down-400 text-xs hover:underline px-3 py-2 sm:px-1 sm:py-1"
                        >
                          remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-fg-subtle mt-1">
            Rungs sit <b>above</b> the golden number when bullish and{" "}
            <b>below</b> it when bearish, so the biggest allocation usually goes
            nearest the number. The percentages are{" "}
            <b>auto-normalised</b> — they do not have to add to 100 (yours sum to{" "}
            {pctSum}
            {hasAggr && ` / ${aggrSum} aggressive`}). A rung whose share rounds
            to zero lots is simply skipped.
          </p>
        </div>
      )}
    </Section>
  );
}

function ValueAreaSection({ patch, has, b }: { patch: Patch; has: Has; b: Bool }) {
  // Only Daily HTP Ladders declares these, so Golden Number never shows it.
  if (!has("flip_on_value_area")) return null;
  const flipOn = b("flip_on_value_area");
  return (
    <Section title="Value area (VAH / VAL)">
      <Field label="Flip on the value area">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={flipOn}
            // The ladders option needs this one, so turning it off clears both
            // rather than posting a combination the strategy rejects.
            onChange={(e) =>
              patch(
                e.target.checked
                  ? { flip_on_value_area: true }
                  : { flip_on_value_area: false, ladders_on_value_area: false },
              )
            }
            className="mt-0.5"
          />
          <span className="text-sm text-fg">
            Flip past VAL &minus; trigger / VAH + trigger
          </span>
        </label>
        <p className="text-xs text-fg-subtle mt-1">
          An open position flips only on a close beyond the opening window&rsquo;s{" "}
          <b>value-area edge</b> plus the trigger, instead of golden &plusmn;
          trigger — more room before a flip. Arming, the ladders and the target
          still measure from the golden number. Needs a profile golden source
          (first-candle or 5-minute HTP); otherwise it falls back to the golden
          band.
        </p>
      </Field>
      {has("ladders_on_value_area") && (
        <Field label="Ladders on the value area">
          <label
            className={`flex items-start gap-2 ${flipOn ? "cursor-pointer" : "opacity-50"}`}
          >
            <input
              type="checkbox"
              checked={b("ladders_on_value_area")}
              disabled={!flipOn}
              onChange={(e) => patch({ ladders_on_value_area: e.target.checked })}
              className="mt-0.5"
            />
            <span className="text-sm text-fg">
              Rungs at VAL + offset (bull) / VAH &minus; offset (bear)
            </span>
          </label>
          <p className="text-xs text-fg-subtle mt-1">
            Moves the entry rungs <b>inside</b> the value area — deeper pullback
            entries than golden &plusmn; offset. Requires the value-area flip
            above, or every fill would sit on the flip level.
          </p>
        </Field>
      )}
      <p className="col-span-1 sm:col-span-2 text-xs text-muted">
        Backtest only for now: paper/live deployments do not compute the value
        area yet, so with these on they trade the plain golden band.
      </p>
    </Section>
  );
}

function TargetSection({ patch, has, n }: { patch: Patch; has: Has; n: Num }) {
  if (!has("target_points") && !has("runner_pct")) return null;
  return (
    <Section
      title="Target, runner and size"
      note="How the position is taken off: most of it at a fixed target, a slice left to trail."
    >
      {has("target_points") && (
        <Field label="Target (points)">
          <NumberInput
            step="any"
            min={0.1}
            max={5000}
            value={n("target_points", 40)}
            onChange={(v) => patch({ target_points: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            Measured from the running average entry of the lots actually filled,
            so a second tranche moves the target with it. Hitting it closes
            everything but the runner and cancels the unfilled rungs.
          </p>
        </Field>
      )}
      {has("runner_pct") && (
        <Field label="Runner (% of the filled position)">
          <NumberInput
            step="any"
            min={0.1}
            max={99.9}
            // Shown as a percentage of the stored fraction. toPrecision(12)
            // only strips float noise (0.07 × 100 = 7.000000000000001), so
            // 12.34 typed is 12.34 shown and 0.1234 saved — no rounding.
            value={Number((n("runner_pct", 0.2) * 100).toPrecision(12))}
            onChange={(v) => patch({ runner_pct: v / 100 })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            Left behind at the target to trail the Supertrend instead. Rounded to
            whole lots, so on a small position it rounds to zero and the target
            simply closes 100%.
          </p>
        </Field>
      )}
      {has("atr_period") && (
        <Field label="Trail: ATR length">
          <NumberInput
            min={2}
            max={200}
            value={n("atr_period", 10)}
            onChange={(v) => patch({ atr_period: v })}
            className={inputClass}
          />
        </Field>
      )}
      {has("supertrend_mult") && (
        <Field label="Trail: Supertrend multiplier">
          <NumberInput
            step="any"
            min={0.1}
            max={20}
            value={n("supertrend_mult", 1)}
            onChange={(v) => patch({ supertrend_mult: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            The runner exits the instant price touches the Supertrend
            (atr_period, multiplier) line — the same line the chart draws in
            purple.
          </p>
        </Field>
      )}
      {has("total_lots") && (
        <Field label="Total lots (fallback)">
          <NumberInput
            min={1}
            max={1000}
            value={n("total_lots", 3)}
            onChange={(v) => patch({ total_lots: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            Only used when no sizer is driving the position. In a deployment the
            Sizing card&rsquo;s lot count wins, so this is normally left alone.
          </p>
        </Field>
      )}
    </Section>
  );
}

function DaySection({
  patch,
  has,
  s,
  n,
  b,
}: {
  patch: Patch;
  has: Has;
  s: Str;
  n: Num;
  b: Bool;
}) {
  const anyGate =
    has("max_loss_mode") ||
    has("loss_recovery") ||
    has("stop_on_profit") ||
    has("max_open_loss_points") ||
    has("max_flips_enabled");
  if (!anyGate) return null;
  const mode = s("max_loss_mode", "fixed_points");
  const lossUnit = s("max_loss_measured_on", "deployment");
  const lossIsRupees = lossUnit === "rupees";

  // Switching the loss-cap unit crosses a ₹/points scale boundary, so reseed
  // max_loss_points to a sensible default rather than leaving e.g. a "50" that
  // meant 50 points now acting as a ₹50 cap.
  function changeLossUnit(next: string) {
    const patchObj: Record<string, unknown> = { max_loss_measured_on: next };
    const cur = n("max_loss_points", 50);
    if (next === "rupees" && cur < 1000) patchObj.max_loss_points = 25000;
    else if (next !== "rupees" && cur >= 1000) patchObj.max_loss_points = 50;
    patch(patchObj);
  }

  return (
    <Section
      title="Day limits"
      note="Every gate here blocks NEW entries only — none of them ever force an open position shut."
    >
      {has("max_loss_mode") && (
        <Field label="Daily loss cap">
          <select
            value={mode}
            onChange={(e) => patch({ max_loss_mode: e.target.value })}
            className={selectClass}
          >
            <option value="fixed_points">A fixed amount I set</option>
            <option value="past_sessions">
              Give back the last few winning sessions
            </option>
          </select>
        </Field>
      )}
      {mode === "past_sessions" && has("max_loss_sessions") && (
        <Field label="How many winning sessions">
          <NumberInput
            min={1}
            max={100}
            value={n("max_loss_sessions", 4)}
            onChange={(v) => patch({ max_loss_sessions: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            The cap is the summed profit of the last this-many net-positive
            sessions. Until that many exist there is <b>no cap at all</b>, which
            is worth knowing on a fresh deployment.
          </p>
        </Field>
      )}
      {mode === "fixed_points" && has("max_loss_measured_on") && (
        <Field label="Cap measured in">
          <select
            value={lossUnit}
            onChange={(e) => changeLossUnit(e.target.value)}
            className={selectClass}
          >
            <option value="deployment">Underlying points (deployment)</option>
            <option value="traded">Option-premium points (traded)</option>
            <option value="rupees">Rupees (absolute ₹)</option>
          </select>
          <p className="text-xs text-fg-subtle mt-1">
            <b>Underlying points (deployment)</b> is recommended, including for
            option routing: since 2026-07-31 it measures the underlying&rsquo;s
            real move from the strategy&rsquo;s own entry/exit prices, so it is
            independent of premium and of lot count. It is also the only measure
            that can block a losing flip&rsquo;s reversal the instant it happens
            — on <b>traded</b>/<b>rupees</b> the loss is not in the position book
            yet when the reversal decides to arm.
          </p>
        </Field>
      )}
      {mode === "fixed_points" && has("max_loss_points") && (
        <Field label={lossIsRupees ? "Max daily loss (₹)" : "Max daily loss (points)"}>
          <NumberInput
            min={lossIsRupees ? 1 : 0.1}
            max={1_000_000}
            step="any"
            value={n("max_loss_points", lossIsRupees ? 25000 : 50)}
            onChange={(v) => patch({ max_loss_points: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            {lossIsRupees
              ? "Stop new entries once the day's realised loss reaches this many rupees."
              : "Stop new entries once the day's loss reaches this many points (÷ lot size × lots)."}
          </p>
        </Field>
      )}
      {has("max_open_loss_points") && (
        <Field label="Square off on day-total loss (points)">
          <NumberInput
            min={0}
            max={1_000_000}
            step="any"
            value={n("max_open_loss_points", 0)}
            onChange={(v) => patch({ max_open_loss_points: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            <b>0 = off.</b> The only limit here that <b>closes a live position</b>
            {" "}— every other one on this card just blocks new entries. It
            watches where the <b>day</b> stands right now: points already
            realised <b>plus</b> the open position marked to the live price. The
            moment that total is this far down, the position is squared off and
            the day ends. This is the gap the max-daily-loss above cannot see —
            at a 50-point cap, a day that has realised &minus;45 is still inside
            its cap while holding a position another &minus;30 under water, so
            the day is really &minus;75 with nothing watching. Set this{" "}
            <b>wider</b> than the max daily loss.
          </p>
        </Field>
      )}
      {has("max_flips_enabled") && (
        <Field label="Cap the day's flips">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={b("max_flips_enabled")}
              onChange={(e) => patch({ max_flips_enabled: e.target.checked })}
              className="mt-0.5"
            />
            <span className="text-sm text-fg">
              Stop after a set number of stop-and-reverse flips
            </span>
          </label>
          {b("max_flips_enabled") && has("max_flips") && (
            <NumberInput
              min={1}
              max={50}
              value={n("max_flips", 2)}
              onChange={(v) => patch({ max_flips: v })}
              className={`${inputClass} mt-2`}
            />
          )}
          <p className="text-xs text-fg-subtle mt-1">
            A flip is any close beyond the <b>opposite</b> band that flattens an
            open position. The flip that reaches the cap still closes the
            position, but the opposite ladder does not arm — so the cap is also
            the number of round trips the day can start. The count itself is
            in-memory, so a mid-day restart resumes from zero flips.
          </p>
        </Field>
      )}
      {has("loss_recovery") && (
        <Field label="Loss recovery target">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={b("loss_recovery")}
              onChange={(e) => patch({ loss_recovery: e.target.checked })}
              className="mt-0.5"
            />
            <span className="text-sm text-fg">
              Stretch the target to win back the day&rsquo;s loss
            </span>
          </label>
          <p className="text-xs text-fg-subtle mt-1">
            Once the day has a booked loss, the next target becomes{" "}
            <b>
              what it takes to earn those rupees back on the position you hold
              now, plus half your normal target
            </b>{" "}
            — never nearer than the normal target. Reverts to the plain target
            once the day is back to break-even. The trailed runner is unaffected.
          </p>
        </Field>
      )}
      {has("stop_on_profit") && (
        <Field label="Stop the day in profit">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={b("stop_on_profit")}
              onChange={(e) => patch({ stop_on_profit: e.target.checked })}
              className="mt-0.5"
            />
            <span className="text-sm text-fg">
              Stop trading once the day is flat and in profit
            </span>
          </label>
          <p className="text-xs text-fg-subtle mt-1">
            The <b>first winning round trip ends the day</b>. On a day that
            started red it ends as soon as the losses are recovered and anything
            is left over — pairs naturally with the loss-recovery target above.
            Checked the instant a position closes, so the same candle cannot
            re-enter. Nothing is forced shut: an open position, including the
            trailed runner, still runs its own target and trail.
          </p>
        </Field>
      )}
    </Section>
  );
}

function SessionSection({ patch, has, s }: { patch: Patch; has: Has; s: Str }) {
  if (!has("session_start_ist")) return null;

  /** An empty value is never written through (TimeInput holds it locally).
   *  Clearing a `<input type="time">` fires onChange with "", which the
   *  strategy parses with `_parse_hhmm` — it used to raise a bare ValueError at
   *  construction, i.e. a 500 on start rather than a field error (now also
   *  rejected server-side). */
  const setTime = (k: string) => (v: string) => patch({ [k]: v });

  return (
    <Section title="Session times (IST)">
      <Field label="Session open">
        <TimeInput
          value={s("session_start_ist", "09:15")}
          onChange={setTime("session_start_ist")}
          className={inputClass}
        />
        <p className="text-xs text-fg-subtle mt-1">
          The first candle from here sets the golden number.
        </p>
      </Field>
      {has("no_new_entry_after_ist") && (
        <Field label="No new entries after">
          <TimeInput
            value={s("no_new_entry_after_ist", "15:00")}
            onChange={setTime("no_new_entry_after_ist")}
            className={inputClass}
          />
        </Field>
      )}
      {has("square_off_ist") && (
        <Field label="Square off everything at">
          <TimeInput
            value={s("square_off_ist", "15:15")}
            onChange={setTime("square_off_ist")}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            A hard flatten — the only gate here that does close an open position.
          </p>
        </Field>
      )}
    </Section>
  );
}

function TimeframeSection({ patch, has, s }: { patch: Patch; has: Has; s: Str }) {
  if (!has("arming_close_tf") && !has("supertrend_tf")) return null;
  const tfSelect = (k: string, dflt = "") => (
    <select
      value={s(k, dflt)}
      onChange={(e) => patch({ [k]: e.target.value })}
      className={selectClass}
    >
      {TF_CHOICES.map((tf) => (
        <option key={tf || "same"} value={tf}>
          {tf || "Same as the run's timeframe"}
        </option>
      ))}
    </select>
  );
  return (
    <Section
      title="Timeframes"
      note="Each of these can run on a coarser or finer candle than the deployment's own timeframe. Blank means they all use the run's timeframe."
    >
      {has("arming_close_tf") && (
        <Field label="Direction / arming close">{tfSelect("arming_close_tf")}</Field>
      )}
      {has("stop_close_tf") && (
        <Field label="Stop / reversal close">
          {tfSelect("stop_close_tf")}
          <p className="text-xs text-fg-subtle mt-1">
            Setting this finer than the arming timeframe reacts to a reversal
            sooner, at the cost of more flips.
          </p>
        </Field>
      )}
      {has("supertrend_tf") && (
        <Field label="Runner's Supertrend">{tfSelect("supertrend_tf")}</Field>
      )}
    </Section>
  );
}

function ResolveSection({ patch, has, n }: { patch: Patch; has: Has; n: Num }) {
  if (!has("resolve_within_points")) return null;
  const within = n("resolve_within_points", 30);
  return (
    <Section
      title="Contract resolution"
      note="Paper/live and option-routed backtests: how fresh the traded contract's pick has to be when the first tranche fills."
    >
      <Field label="Resolve within (points of an unfilled rung)">
        <NumberInput
          step="any"
          min={0}
          max={1000}
          value={within}
          onChange={(v) => patch({ resolve_within_points: v })}
          className={inputClass}
        />
        <p className="text-xs text-fg-subtle mt-1">
          The strike is still fixed at the <b>first fill</b> and reused for every
          later tranche and the exits — this only controls how stale the pick is
          allowed to be when that fill lands. <b>0</b> resolves once, the moment
          a ladder arms, which is what let a price-range pick drift below its own
          band while the ladder waited.
        </p>
      </Field>
      {has("resolve_refresh_seconds") && within > 0 && (
        <Field label="Re-resolve every (seconds)">
          <NumberInput
            min={5}
            max={3600}
            value={n("resolve_refresh_seconds", 30)}
            onChange={(v) => patch({ resolve_refresh_seconds: v })}
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            Each refresh costs one option-chain fetch, so this trades broker
            quota against staleness.
          </p>
        </Field>
      )}
    </Section>
  );
}

function RoutingSection({
  patch,
  has,
  s,
  n,
}: {
  patch: Patch;
  has: Has;
  s: Str;
  n: Num;
}) {
  if (!has("order_instrument")) return null;
  const orderInstrument = s("order_instrument", "option");
  const moneyness = s("option_moneyness", "ATM");
  return (
    <Section
      title="Order routing"
      note="Where the orders are placed — the signal always runs on the deployment (underlying) symbol."
    >
      <Field label="Place orders on">
        <select
          value={orderInstrument}
          onChange={(e) => patch({ order_instrument: e.target.value })}
          className={selectClass}
        >
          <option value="option">Option (buy CE for long / PE for short)</option>
          <option value="future">Future</option>
          <option value="spot">Deployment symbol (spot)</option>
        </select>
      </Field>

      {orderInstrument !== "spot" && has("option_underlying") && (
        <Field label="F&O underlying root">
          <input
            value={s("option_underlying")}
            onChange={(e) => patch({ option_underlying: e.target.value })}
            placeholder="blank = auto (NIFTY 50 → NIFTY)"
            {...noAutocorrect}
            autoCapitalize="characters"
            className={inputClass}
          />
          <p className="text-xs text-fg-subtle mt-1">
            Only needed when the derivative root differs from the deployment
            symbol and is not a known NSE index — blank auto-maps those and
            otherwise uses the symbol as-is.
          </p>
        </Field>
      )}

      {orderInstrument === "future" && (
        <Field label="Future contract">
          <select
            value={s("future_symbol") ? "fixed" : "near"}
            onChange={(e) =>
              patch({ future_symbol: e.target.value === "near" ? "" : " " })
            }
            className={selectClass}
          >
            <option value="near">Near month (auto-roll)</option>
            <option value="fixed">A specific contract</option>
          </select>
        </Field>
      )}

      {orderInstrument === "future" && s("future_symbol") !== "" && (
        <Field label="Contract symbol">
          <input
            value={s("future_symbol").trim()}
            // Emptying the box must not flip the dropdown back to "Near month"
            // and hide it mid-edit: " " is the same "specific contract, not yet
            // typed" marker the dropdown writes. It is still blank to the
            // router (it strips), so `required` blocks saving it that way —
            // otherwise the screen would say "a specific contract" while the
            // deployment auto-rolled.
            onChange={(e) => patch({ future_symbol: e.target.value || " " })}
            required
            placeholder="e.g. NIFTY26JULFUT"
            {...noAutocorrect}
            autoCapitalize="characters"
            className={inputClass}
          />
        </Field>
      )}

      {orderInstrument === "future" && s("future_symbol") === "" && (
        <p className="col-span-1 sm:col-span-2 text-xs text-muted">
          The near-month contract is re-resolved from your broker&rsquo;s
          instrument list on every entry, so this deployment rolls to the next
          contract by itself at expiry. This strategy squares off intraday, so it
          is always flat when the roll happens.
        </p>
      )}

      {orderInstrument === "option" && (
        <>
          <Field label="Strike selection">
            <select
              value={moneyness}
              onChange={(e) => patch({ option_moneyness: e.target.value })}
              className={selectClass}
            >
              <option value="ATM">ATM (nearest strike)</option>
              <option value="ITM">ITM (in-the-money)</option>
              <option value="price_range">Price range (cheapest in band)</option>
            </select>
          </Field>
          {moneyness === "ITM" && (
            <Field label="ITM depth (strikes)">
              <NumberInput
                min={1}
                max={20}
                value={n("itm_depth", 1)}
                onChange={(v) => patch({ itm_depth: v })}
                className={inputClass}
              />
            </Field>
          )}
          {moneyness === "price_range" && (
            <>
              <Field label="Min premium">
                <NumberInput
                  step="any"
                  min={0}
                  value={n("price_min", 0)}
                  onChange={(v) => patch({ price_min: v })}
                  className={inputClass}
                />
              </Field>
              <Field label="Max premium">
                <NumberInput
                  step="any"
                  min={0}
                  value={n("price_max", 0)}
                  onChange={(v) => patch({ price_max: v })}
                  className={inputClass}
                />
                {n("price_max", 0) < n("price_min", 0) && (
                  <p className="text-xs text-down-600 dark:text-down-400 mt-1">
                    Max premium must be at least the min — the strategy refuses
                    this pair.
                  </p>
                )}
              </Field>
            </>
          )}
          <Field label="Expiry">
            <select
              value={s("expiry_type", "weekly")}
              onChange={(e) => patch({ expiry_type: e.target.value })}
              className={selectClass}
            >
              <option value="weekly">Nearest weekly</option>
              <option value="monthly">Nearest monthly</option>
            </select>
          </Field>
        </>
      )}

      <Field label="Target measured on">
        <select
          value={s("target_on", "deployment")}
          onChange={(e) => patch({ target_on: e.target.value })}
          className={selectClass}
        >
          <option value="deployment">Deployment symbol (underlying)</option>
          <option value="traded">Traded contract</option>
        </select>
      </Field>
      <Field label="Trailing measured on">
        <select
          value={s("trail_on", "deployment")}
          onChange={(e) => patch({ trail_on: e.target.value })}
          className={selectClass}
        >
          <option value="deployment">Deployment symbol (underlying)</option>
          <option value="traded">Traded contract</option>
        </select>
      </Field>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* layout                                                              */
/* ------------------------------------------------------------------ */

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-sm font-medium text-fg border-b border-border/60 pb-1 mb-3">
        {title}
      </div>
      {note && <p className="text-xs text-muted -mt-2 mb-3">{note}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg-subtle mb-1">{label}</label>
      {children}
    </div>
  );
}
